/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import openapi from "@zkt/open-api";
import moment from "moment";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { AxiosRequestConfig } from "axios";
import { fromEvent, merge } from "rxjs";
import { filter, map, take, skip, distinctUntilChanged } from "rxjs/operators";
import {
  http,
  merchant$,
  auth$,
  language$,
  userRights$,
  user$,
  channels$,
  Channels,
  Namespace,
  Auth,
  User,
  LocalStorage,
  login,
  TokenService,
  router,
  config,
  logout,
  Brand,
  BrandRelation,
  BrandRelationTypes,
  Merchant,
  PromiseReturnType,
  Language,
  Currency,
  CentralBookConfig,
} from "@zkt/eb-utility";
import {
  init,
  setGlobalProperties,
  autoTrackSinglePage,
  setAnonymousID,
} from "@zkt/tracker";
import { UserGuide, message } from "@zkt/components";
import { start } from "single-spa";
import {
  getEbUserUserGuideStatus,
  getEbUserUserGuideViewed,
} from "@zkt/open-api/esm/user-api";
import { getEbUserUserInfoHotels } from "@zkt/open-api/esm/oauth2";
import {
  getEbConfigGetCentralBookConfig,
  getEbHotelCtxQuery,
  getEbConfigOpenHotelPromotionChannel,
} from "@zkt/open-api/esm/merchant";
import GlobalSatisfactionWin from "@zkt/eb-feedback";
import { iframeMessage$ } from "./observables/iframe-message";

function getUser() {
  return getEbUserUserInfoHotels().then(
    ({
      id,
      username,
      realName,
      phone,
      email,
      userType,
      needShowGuide,
      channelCode,
    }) => ({
      userId: id,
      username,
      realname: realName,
      phone,
      userType,
      email,
      needShowGuide,
      channelCode,
    })
  );
}

class Relation {
  appId: string;

  appName: string;

  appType: number;

  brandId: number;

  brandName: string;

  merchantId: number;

  merchantName: string;

  relationType: number;

  saleClientId: number;
}

function convertBrandRelations(merchant: Merchant, items: Relation[]) {
  const { brand } = merchant;
  // 新增多集团关系
  const isMaster = brand.brandType === "brand" && brand.owned;
  function getDefaultData() {
    const options = new BrandRelation();
    Object.assign(options, {
      brandId: brand.brandId,
      brandName: brand.brandName,
      appId: brand.wxAppId,
      merchantName: merchant.merchantName,
      merchantId: merchant.merchantId,
      type: BrandRelationTypes.solo,
    });
    if (brand.wxaHomeAppId) {
      options.appletsAppId = brand.wxaHomeAppId;
    }
    if (isMaster) {
      options.type = BrandRelationTypes.primary;
    }
    return options;
  }
  const defaultRelations: BrandRelation[] = [];
  const relations = {
    // 公众号
    publicAccount: { value: 1, relations: [...defaultRelations] },
    // 小程序标识
    applets: { value: 2, relations: [...defaultRelations] },
    // 抖音
    tikTok: { value: 8, relations: [...defaultRelations] },
    // 支付宝
    alipay: { value: 5, relations: [...defaultRelations] },
    // 小红书
    xhs: { value: 10, relations: [...defaultRelations] },
  };
  // 有公众号或绑定集团
  if (items.length) {
    const appTypes = Object.keys(relations).map((k) => relations[k].value);
    items.forEach((v) => {
      if (
        v.merchantId === merchant.merchantId &&
        v.appType === relations.publicAccount.value
      ) {
        // 当前商户的id与多集团关系的商户id相等，证明是当前商户，赋值集团id
        Object.assign(merchant, { brandId: v.brandId });
      }
      if (appTypes.includes(v.appType)) {
        const options = {
          brandName: v.brandName,
          brandId: v.brandId,
          appName: v.appName,
          appId: v.appId,
          merchantName: v.merchantName,
          merchantId: v.merchantId,
        };
        const relation = Object.values(relations).find(
          (x) => x.value === v.appType
        ).relations;
        if (v.relationType === 1) {
          const obj = Object.assign(new BrandRelation(), {
            ...options,
            type: isMaster
              ? BrandRelationTypes.master
              : BrandRelationTypes.owner,
            // brandName: brand.brandName,
          });
          if (v.appType === relations.publicAccount.value) {
            relation.unshift(obj);
          } else {
            relation.push(obj);
          }
        }
        if (v.relationType === 2) {
          const obj = Object.assign(new BrandRelation(), {
            ...options,
            type: BrandRelationTypes.brand,
          });
          relation.push(obj);
        }
      }
    });
    relations.publicAccount.relations.forEach((item) => {
      relations.applets.relations.forEach((a) => {
        if (a.brandId === item.brandId) {
          Object.assign(item, { appletsAppId: a.appId });
        }
      });
    });
    // app_type都不为1时，没有多集团关系
    if (relations.publicAccount.relations.length === 0) {
      relations.publicAccount.relations.push(getDefaultData());
    }
  } else {
    // 默认数据
    // 无公众号且没有绑集团
    // 单店
    const options = getDefaultData();
    relations.publicAccount.relations.push(options);
    relations.applets.relations.push(options);
  }
  const result = {
    publicAccount: relations.publicAccount.relations,
    applets: relations.applets.relations,
    tikTok: relations.tikTok.relations,
    alipay: relations.alipay.relations,
    xhs: relations.xhs.relations,
  };
  return result;
}

function getUserRights(hotelId: number, userId: number) {
  return http
    .request({
      url: "restful/bmw_user/user_rights",
      params: { hotel_id: hotelId, user_id: userId },
    })
    .then((res) => res.data.response.result)
    .catch(() => []);
}

function convertMerchant(
  id: number,
  data: PromiseReturnType<typeof getEbHotelCtxQuery>
): Merchant {
  const {
    brandInfo,
    merchantConfigs,
    merchantInfo,
    ownerPromotionChannel,
    distributionPromotionChannel,
  } = data;
  const merchant = new Merchant();
  merchant.address = merchantInfo.address;
  merchant.brandId = merchantInfo.brandId;
  merchant.cooperationPattern = merchantInfo.cooperationPattern;
  merchant.industryId = merchantInfo.industryId;
  merchant.officeDepartmentId = merchantInfo.officeDepartmentId;
  merchant.phone = merchantInfo.phone;
  merchant.phoneArea = merchantInfo.phoneArea;
  Object.assign(merchant, {
    merchantName: merchantInfo.hotelName,
    merchantId: id,
  });
  merchant.configs = merchantConfigs as Record<string, string>;
  const brand = new Brand();
  brand.brandId = brandInfo.brandId;
  brand.brandName = brandInfo.brandName;
  brand.brandType = brandInfo.brandType;
  brand.dealOrders = brandInfo.dealOrders;
  brand.wxAppId = brandInfo.wxAppId;
  brand.wxaHomeAppId = brandInfo.wxaHomeAppId;
  brand.prepayCardMasterHotelId = brandInfo.prepayCardMasterHotelId;
  Object.assign(brand, {
    owned: brandInfo.ownByHotelId === merchant.merchantId,
    ownerId: brandInfo.ownByHotelId,
  });
  merchant.brand = brand;
  merchant.main = brand.owned;
  const relations: Array<Relation> = [];
  data.ownerChannel.forEach((c) =>
    relations.push(Object.assign(new Relation(), c, { relationType: 1 }))
  );
  data.distributionChannel.forEach((c) =>
    relations.push(Object.assign(new Relation(), c, { relationType: 2 }))
  );
  merchant.brandRelations = convertBrandRelations(merchant, relations);
  merchant.ownerPromotionChannel = ownerPromotionChannel;
  merchant.distributionPromotionChannel = distributionPromotionChannel;
  const corp = relations.find(
    (x) =>
      x.merchantId === merchant.merchantId &&
      x.appType === 7 &&
      x.relationType === 1
  );
  if (corp) {
    merchant.corpId = corp.appId;
  }
  let merchantCurrencys: Array<Currency> = [];
  if (data.merchantCurrencys) {
    merchantCurrencys = [...merchantCurrencys, ...data.merchantCurrencys];
  }
  merchant.merchantCurrencys = merchantCurrencys;
  return merchant;
}

/**
 * 获取商户信息
 * @param id 商户ID
 */
function getMerchant(merchantId: number): Promise<Merchant> {
  return Promise.all([
    getEbHotelCtxQuery({ merchantId }),
    getEbConfigGetCentralBookConfig({ hotelId: merchantId }),
  ]).then(([res1, res2]) => {
    return {
      ...convertMerchant(merchantId, res1),
      centralBookConfig: res2 as CentralBookConfig,
    };
  });
}

/**
 *
 */
function getChannels(merchant: Merchant): Promise<Channels> {
  return getEbConfigOpenHotelPromotionChannel().then((res): Channels => {
    if (res) {
      if (merchant.centralBookConfig.centralBook * 1) {
        return merchant.centralBookConfig.channel;
      }
      const channels = [];

      // 首先检查是否有分销渠道
      if (merchant.distributionPromotionChannel) {
        merchant.distributionPromotionChannel.forEach((item) => {
          if (item.channel && Array.isArray(item.channel)) {
            channels.push(...item.channel);
          }
        });
      }

      // 然后添加自有渠道
      if (
        merchant.ownerPromotionChannel &&
        merchant.ownerPromotionChannel.channel
      ) {
        channels.push(...merchant.ownerPromotionChannel.channel);
      }

      // 去重并返回
      return [...new Set(channels)];
    }
    return [];
  });
}

export function startup(): void {
  moment.fn.toJSON = function toJSON() {
    return this.format("YYYY-MM-DD HH:mm:ss");
  };
  fromEvent<PromiseRejectionEvent>(window, "unhandledrejection")
    .pipe(filter((e) => e.reason.message))
    .subscribe((e) => message.error(e.reason.message));

  const storage = new LocalStorage(Namespace.GLOBAL);

  // 老页面（iframe）埋点配置，项目启动或者路由变化时手动上报页面浏览信息
  merge(fromEvent(window, "popstate"), fromEvent(window, "load"))
    .pipe(filter(() => window.location.pathname === "/"))
    .subscribe(() => {
      setGlobalProperties({
        first_page: "",
        second_page: "",
        third_page: "",
        name: "",
      });
      autoTrackSinglePage();
    });

  // 新手引导配置
  UserGuide.defaultProps.shownWhen = function shownWhen({ key }) {
    if (window.screen.width < 1200) return false;
    if (!auth$.value) return false;
    const { userId } = auth$.value;
    const cacheKey = `user-guide-${userId}-${key}`;
    if (storage.getItem(cacheKey) === "true") return false;
    return getEbUserUserGuideStatus({ guideName: key })
      .then((result) => {
        storage.setItem(cacheKey, String(result));
        return !result;
      })
      .catch(() => false);
  };

  UserGuide.defaultProps.onClose = function onClose({ key }): void {
    if (!auth$.value) return;
    const { userId } = auth$.value;
    const cacheKey = `user-guide-${userId}-${key}`;
    storage.setItem(cacheKey, "true");
    getEbUserUserGuideViewed({ guideName: key, guideState: 1 });
  };

  // 初始化埋点信息
  init({
    debug: true || config.debugging, // 开启会打印日志
    platform: "pc",
    project: "ebooking",
    serverUrl: `https://${config.logHost}`,
    spa: false,
    clickMap: true, // 自动点击埋点
    scrollNoticeMap: true, // 自动视区埋点
    disableVtrack: true, // 是否关掉拉取可视化全埋点数据
  });

  // 设置埋点全局属性
  setGlobalProperties(
    {
      zkt_platform: "pc",
      user_id_bury() {
        return auth$.value?.userId;
      },
      hotel_id_bury() {
        return auth$.value?.merchantId;
      },
    },
    false
  );

  // 酒店变化时，跳转到首页，忽略首次加载，忽略相同酒店变化
  auth$
    .pipe(filter((x) => !!x))
    .pipe(map((x) => x.merchantId))
    .pipe(distinctUntilChanged((x, y) => x === y))
    .pipe(skip(1))
    .subscribe(() => router.push("/"));

  // iframe内部退出或者身份过期时同步退出登录
  iframeMessage$
    .pipe(filter((x) => x.data?.name === "logout"))
    .subscribe(() => logout());

  // 禁用number文本输入框的滚轮事件
  const ael = () => document.activeElement as HTMLInputElement;
  fromEvent<WheelEvent>(document, "wheel")
    .pipe(filter(() => ael()?.type === "number"))
    .subscribe(() => ael().blur());

  // 其它浏览器标签切换酒店时，同步更新
  fromEvent<StorageEvent>(window, "storage")
    .pipe(filter((x) => x.key === storage.getKey("token")))
    .pipe(filter((x) => x.oldValue !== x.newValue))
    .subscribe((ev) => {
      const token = ev.newValue;
      const params = new TokenService().decrypt(token);
      const auth = params ? new Auth(token, params) : null;
      if (!auth$.value || auth?.merchantId !== auth$.value.merchantId) {
        auth$.next(auth);
      }
    });

  // language$ 订阅
  // 更新moment语言
  dayjs.locale("zh-cn");
  // language$.subscribe((lang) => moment.locale(lang));
  // 切换语言时保存语言
  language$.subscribe((lang) => {
    const l = Object.values(Language).includes(lang) ? lang : Language.ZH_CN;
    storage.setItem("language", l);
  });
  // 设置@zkt/open-api请求方法
  openapi.request = function request(options) {
    if (options.query?.hotelId) {
      // 同步hotelId和merchantId
      Object.assign(options.query, { merchantId: options.query?.hotelId });
    }
    // 初始化api请求方法
    return http.request({
      ...options,
      params: options.query,
      headers: options.header,
      data: options.body,
    } as AxiosRequestConfig);
  };

  // 用户更新时更新埋点信息
  auth$
    .pipe(filter((x) => !!x?.userId))
    .subscribe((x) => setAnonymousID(`eb-${x?.userId}-${x?.merchantId}`));

  // 尝试登录
  auth$.next(login({}));
  auth$.subscribe((x) => (x ? start() : logout()));

  auth$.subscribe((auth) => {
    if (auth) {
      http.defaults.params = {
        userId: auth.userId,
        hotelId: auth.merchantId,
        brandId: auth.brandId,
        merchantId: auth.merchantId,
      };
      new TokenService().set(auth.token);
      getUserRights(auth.merchantId, auth.userId).then((rights) => {
        if (auth$.value?.merchantId === auth.merchantId) {
          userRights$.next(rights);
        }
      });
      getMerchant(auth.merchantId)
        .then((merchant) => {
          if (auth$.value?.merchantId === merchant.merchantId) {
            merchant$.next(merchant);
            getChannels(merchant)
              .then((channels) => {
                channels$.next(channels);
              })
              .catch((err) => channels$.error(err));
          } else {
            // window.location.reload();
          }
        })
        .catch((err) => merchant$.error(err));
    } else {
      http.defaults.params = {};
      merchant$.next(null);
      userRights$.next(null);
      channels$.next(null);
    }
    if (!user$.closed && auth) {
      // 用户还没加载时才去获取，不会重复加载
      const user = new User();
      getUser()
        .then((data) => Object.assign(user, data))
        .then((u) => {
          user$.next(u);
        })
        .then(() => user$.complete())
        .catch((err) => user$.error(err));
    } else {
      user$.next(null);
      user$.complete();
    }
  });

  // 加载满意度插件
  const loadPlugin = () => {
    const inIframe = window.top !== window;
    // 判断是否iframe加载的页面,是则不显示满意度插件
    if (!inIframe) {
      // eslint-disable-next-line no-new
      new GlobalSatisfactionWin(
        openapi.request,
        "react",
        auth$.value?.merchantId
      );
    }
  };
  // 页面变化加载
  fromEvent<PopStateEvent>(window, "popstate").subscribe(() => loadPlugin());
  // 首次加载加载
  auth$
    .pipe(filter((x) => !!x))
    .pipe(take(1))
    .subscribe(loadPlugin);
}
